'use client';


/** Imports */
import {useTRPCClient} from '@/trpc/client';
import {type Theme as SelectTheme} from 'react-select';
import {zUserTheme} from '@/db/schema/user-preferences';
import React, {createContext, useState, useEffect, useCallback, useMemo} from 'react';


/** Types */
type Theme = 'light' | 'dark';
type SetThemeOptions = {
    markChoice?: boolean;
    persist?: boolean;
};
export type SetThemeResult = {
    success: boolean;
    error?: string;
};
type ContextType = {
    theme: Theme;
    setTheme: (theme: Theme, options?: SetThemeOptions) => Promise<SetThemeResult>;
    selectTheme: SelectTheme | undefined;
    selectThemeV2: SelectTheme | undefined;
};
type Props = Readonly<{
    children: React.ReactNode;
    initialTheme: Theme;
    isAuthenticated: boolean;
}>;


/** Constants */
export const ThemeContext = createContext<ContextType>(null as any);
const emptySetThemeResult: SetThemeResult = {
    success: true,
};

const selectTheme: SelectTheme = {
    borderRadius: 8,
    colors: {
        primary: 'rgb(var(--color-primary-light) / .75)',
        primary75: 'rgb(var(--color-primary-light) / .75)',
        primary50: 'rgb(var(--color-primary-light) / .5)',
        primary25: 'rgb(var(--color-primary-light) / .25)',
        danger: 'red',
        dangerLight: 'red',
        neutral0: 'rgb(var(--color-form-background))', //       <-- Input background color
        neutral5: 'rgb(var(--color-disabled-lighter))', //      <-- Disabled input background color
        neutral10: 'rgb(var(--color-primary-light))', //        <-- Inner border color
        neutral20: 'rgb(var(--color-primary-light))', //        <-- Border color
        neutral30: 'rgb(var(--color-primary-light))', //        <-- Hover border
        neutral40: 'rgb(var(--color-primary-light) / .75)', //  <-- Hover
        neutral50: 'rgb(var(--color-gray-button-text))', //     <-- Placeholder color
        neutral60: 'rgb(var(--color-primary-light))',
        neutral70: 'rgb(var(--color-primary-light))',
        neutral80: 'rgb(var(--color-primary-light))', //        <-- Text color inside value
        neutral90: 'rgb(var(--color-primary-light))',
    },
    spacing: {
        baseUnit: 4,
        controlHeight: 40,
        menuGutter: 8,
    },
};
const selectThemeV2: SelectTheme = {
    borderRadius: 8,
    colors: {
        primary: 'rgb(var(--color-primary-light) / .75)',
        primary75: 'rgb(var(--color-primary-light) / .75)',
        primary50: 'rgb(var(--color-primary-light) / .5)',
        primary25: 'rgb(var(--color-primary-light) / .25)',
        danger: 'red',
        dangerLight: 'red',
        neutral0: 'rgb(var(--color-form-background))', //       <-- Input background color
        neutral5: 'rgb(var(--color-disabled-lighter))', //      <-- Disabled input background color
        neutral10: 'rgb(var(--color-primary-light))', //        <-- Inner border color
        neutral20: 'rgb(var(--color-primary-light))', //        <-- Border color
        neutral30: 'rgb(var(--color-primary-light))', //        <-- Hover border
        neutral40: 'rgb(var(--color-primary-light) / .75)', //  <-- Hover
        neutral50: 'rgb(var(--color-gray-button-text))', //     <-- Placeholder color
        neutral60: 'rgb(var(--color-primary-light))',
        neutral70: 'rgb(var(--color-primary-light))',
        neutral80: 'rgb(var(--color-primary-light))', //        <-- Text color inside value
        neutral90: 'rgb(var(--color-primary-light))',
    },
    spacing: {
        baseUnit: 4,
        controlHeight: 28,
        menuGutter: 8,
    },
};


/** Component */
export function ThemeProvider(props: Props) {
    const {children, initialTheme, isAuthenticated} = props;

    // Hooks
    const trpcClient = useTRPCClient();

    // State
    const [theme, setTheme] = useState<Theme>(initialTheme);

    // Memos
    const applyTheme = useCallback((newTheme: Theme) => {
        localStorage.setItem('mediafox/theme', newTheme);
        document.body.classList.remove('light-mode', 'dark-mode');
        document.body.classList.add(`${newTheme}-mode`);
        setTheme(newTheme);
    }, []);
    const setNewTheme = useCallback(async (newTheme: Theme, options: SetThemeOptions = {}): Promise<SetThemeResult> => {
        const {markChoice = true, persist = true} = options;
        if (!zUserTheme.safeParse(newTheme).success) {
            return {
                success: false,
                error: 'Invalid theme',
            };
        }

        // Update browser state
        if (newTheme !== theme) applyTheme(newTheme);

        // Update user preferences in background
        if (!persist || !isAuthenticated) return emptySetThemeResult;
        try {
            await trpcClient.userPreferences.updateTheme.mutate({theme: newTheme, markChoice});
            return {success: true};
        } catch (error) {
            return {
                success: false,
                error: (error instanceof Error) ? error.message : 'Failed to save theme preference',
            };
        }
    }, [applyTheme, isAuthenticated, theme, trpcClient]);
    const contextValue = useMemo(() => ({
        theme, setTheme: setNewTheme,
        selectTheme, selectThemeV2,
    }), [theme, setNewTheme]);

    // Effects
    useEffect(() => {

        // Get theme from local storage
        if (isAuthenticated) return;
        const themeFromCache = localStorage.getItem('mediafox/theme');
        const parsedThemeFromCache = zUserTheme.safeParse(themeFromCache);
        if (parsedThemeFromCache.success) applyTheme(parsedThemeFromCache.data);
    }, [applyTheme, isAuthenticated]);


    // Render
    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
}
